import { FontSizeTypes } from '@types/theme';
import React, { FC, MouseEventHandler, ReactChild, isValidElement } from 'react';
import styled from 'styled-components';

import IconArrowDown from '@assets/vectors/icon-arrow-side-full.svg';
import IconCopy from '@assets/vectors/icon-copy.svg';

import theme from '@theme/WsTheme';

import Eyebrow from '../Ws-Eyebrow';

type IBaseButtonProps = {
	className?: string;
	label: string | ReactChild;
	disabled?: boolean;
	href?: string | URL;
	labelSize?: FontSizeTypes;
	newTab?: boolean;
	type?: 'button' | 'submit' | 'reset';
	onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

export type IButtonProps = {
	className?: string;
	variant?:
		| 'default'
		| 'secondary'
		| 'knockout'
		| 'jumplink'
		| 'jumplink-primary'
		| 'back'
		| 'copy';
	disabled?: boolean;
	href?: string;
	newTab?: boolean;
	type?: 'button' | 'submit' | 'reset' | undefined;
	label: string | ReactChild;
	labelSize?: FontSizeTypes;
	onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

const ButtonLabel = styled(Eyebrow)`
	padding: 0 2rem;
`;

const BaseButton: FC<IBaseButtonProps> = ({
	className,
	href,
	label,
	labelSize = 'sm',
	newTab,
	type,
	onClick = () => {},
	disabled = false,
}) => {
	const Component = href ? 'a' : 'button';

	if (isValidElement(label))
		return (
			<Component
				className={className}
				disabled={disabled}
				href={href}
				onClick={onClick}
				target={newTab ? '_blank' : undefined}
				type={type ?? 'button'}>
				{label}
			</Component>
		);

	return (
		<Component
			className={className}
			disabled={disabled}
			href={href}
			onClick={onClick}
			target={newTab ? '_blank' : undefined}
			type={type ?? 'button'}>
			<ButtonLabel size={labelSize}>{label}</ButtonLabel>
		</Component>
	);
};

const DefaultButton = styled(BaseButton)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 6rem;
	background: ${theme.colors.black};
	border: 0;
	border-radius: ${theme.radius.xxl};
	padding: 0;
	transition: all ease-in 0.2s, outline-width linear 0s;
	text-decoration: none;
	white-space: nowrap;
	&:hover {
		cursor: pointer;
		background: ${theme.colors.cyan};
		transition: all ease-out 0.2s, outline-width linear 0s;
		${ButtonLabel}, ${Eyebrow} {
			color: ${theme.colors.black};
		}
	}

	${ButtonLabel}, ${Eyebrow} {
		font-family: 'Montserrat';
		color: ${theme.colors.white};
		font-size: ${theme.fontSizes.body.label};
		letter-spacing: 0;
		font-weight: normal;
	}

	&:disabled {
		background: ${theme.colors.darkGray};

		${ButtonLabel}, ${Eyebrow} {
			color: ${theme.transparency.transparency50(theme.colors.black)};
		}
	}

	&:disabled:hover {
		background: ${theme.colors.darkGray};

		${ButtonLabel}, ${Eyebrow} {
			color: ${theme.transparency.transparency50(theme.colors.black)};
		}
	}
`;

const SecondaryButton = styled(DefaultButton)`
	background: ${theme.colors.black};
	transition: all ease-in 0.2s, outline-width linear 0s;
	height: 4.8rem;
`;

const SecondaryKnockoutButton = styled(SecondaryButton)`
	background: transparent;
	border: 1.5px solid ${theme.colors.black};

	${ButtonLabel}, ${Eyebrow} {
		color: ${theme.colors.black};
	}

	&:hover {
		border: 1.5px solid ${theme.colors.cyan};
	}
`;

const JumplinkButton = styled(BaseButton)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 6rem;
	background: ${theme.colors.transparent};
	border: 0;
	border-radius: ${theme.radius.xxl};
	padding: 0 1.5rem;
	border: 1.5px solid ${theme.colors.black};
	transition: all ease-in 0.2s, outline-width linear 0s;
	text-decoration: none;
	cursor: pointer;
	&::after {
		content: '';
		width: 2.4rem;
		height: 2.4rem;
		display: block;
		background: url(${IconArrowDown}) center no-repeat;
		transform: rotate(-90deg);
		margin-left: 0.5rem;
	}

	${ButtonLabel}, ${Eyebrow} {
		font-family: 'Montserrat';
		color: ${theme.colors.black};
		font-size: ${theme.fontSizes.body.label};
		letter-spacing: 0;
	}

	&:hover {
		border: 1.5px solid ${theme.colors.cyan};
		background: ${theme.colors.cyan};
	}
`;

const JumplinkButtonPrimary = styled(BaseButton)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 6rem;
	background: ${theme.colors.black};
	border: 0;
	border-radius: ${theme.radius.xxl};
	padding: 0 1.5rem;
	border: 1.5px solid ${theme.colors.black};
	transition: all ease-in 0.2s, outline-width linear 0s;
	text-decoration: none;
	cursor: pointer;
	&::after {
		content: '';
		width: 2.4rem;
		height: 2.4rem;
		display: block;
		transform: rotate(-90deg);
		margin-left: 0.5rem;
		background-color: ${theme.colors.white};
		-webkit-mask-image: url(${IconArrowDown});
		mask-image: url(${IconArrowDown});
	}

	${ButtonLabel}, ${Eyebrow} {
		font-family: 'Montserrat';
		color: ${theme.colors.white};
		font-size: ${theme.fontSizes.body.label};
		letter-spacing: 0;
	}

	&:hover {
		border: 1.5px solid ${theme.colors.cyan};
		background: ${theme.colors.cyan};
		& * {
			color: ${theme.colors.black};
		}
		&::after {
			background-color: ${theme.colors.black};
		}
	}
`;

const CopyButton = styled(BaseButton)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 4.8rem;
	background: ${theme.colors.transparent};
	border: 0;
	border-radius: ${theme.radius.xxl};
	padding: 0 1.5rem;
	border: 1.5px solid ${theme.colors.black};
	transition: all ease-in 0.2s, outline-width linear 0s;
	text-decoration: none;
	cursor: pointer;
	&::after {
		content: '';
		width: 2.4rem;
		height: 2.4rem;
		display: block;
		background: url(${IconCopy}) center no-repeat;
		margin-left: 0.5rem;
	}

	${ButtonLabel}, ${Eyebrow} {
		font-family: 'Montserrat';
		color: ${theme.colors.black};
		font-size: ${theme.fontSizes.body.label};
		letter-spacing: 0;
	}

	&:hover {
		border: 1.5px solid ${theme.colors.cyan};
		background: ${theme.colors.cyan};
	}
`;

const BackButton = styled(BaseButton)`
	display: inline-flex;
	align-items: center;
	justify-content: space-between;
	width: auto;
	height: 2.8rem;
	background: ${theme.colors.transparent};
	border-radius: ${theme.radius.xxl};
	padding: 0 0rem;
	border: 1.5px solid ${theme.colors.black};
	transition: all ease-in 0.2s, outline-width linear 0s;
	text-decoration: none;
	cursor: pointer;
	&::before {
		content: '';
		width: 2.4rem;
		height: 2.4rem;
		display: block;
		background: url(${IconArrowDown}) center no-repeat;
		margin-left: 0.5rem;
	}

	${ButtonLabel}, ${Eyebrow} {
		font-family: 'Montserrat';
		color: ${theme.colors.black};
		font-size: ${theme.fontSizes.body.label};
		letter-spacing: 0;
	}

	&:hover {
		border: 1.5px solid ${theme.colors.cyan};
		background: ${theme.colors.cyan};
	}
`;

const Button: FC<IButtonProps> = ({
	label,
	labelSize,
	variant,
	type,
	newTab,
	href,
	onClick,
	className,
	disabled,
}) => {
	if (variant === 'secondary') {
		return (
			<SecondaryButton
				href={href}
				type={type}
				newTab={newTab}
				onClick={onClick}
				label={label}
				labelSize={labelSize}
				className={className}
				disabled={disabled}
			/>
		);
	}
	if (variant === 'knockout') {
		return (
			<SecondaryKnockoutButton
				href={href}
				type={type}
				newTab={newTab}
				onClick={onClick}
				label={label}
				labelSize={labelSize}
				className={className}
				disabled={disabled}
			/>
		);
	}

	if (variant === 'jumplink') {
		return (
			<JumplinkButton
				href={href}
				type={type}
				newTab={newTab}
				onClick={onClick}
				label={label}
				labelSize={labelSize}
				className={className}
				disabled={disabled}
			/>
		);
	}

	if (variant === 'jumplink-primary') {
		return (
			<JumplinkButtonPrimary
				href={href}
				type={type}
				newTab={newTab}
				onClick={onClick}
				label={label}
				labelSize={labelSize}
				className={className}
				disabled={disabled}
			/>
		);
	}

	if (variant === 'copy') {
		return (
			<CopyButton
				href={href}
				type={type}
				newTab={newTab}
				onClick={onClick}
				label={label}
				labelSize={labelSize}
				className={className}
				disabled={disabled}
			/>
		);
	}

	if (variant === 'back') {
		return (
			<BackButton
				href={href}
				type={type}
				newTab={newTab}
				onClick={onClick}
				label={label}
				labelSize={labelSize}
				className={className}
				disabled={disabled}
			/>
		);
	}

	return (
		<DefaultButton
			href={href}
			type={type}
			newTab={newTab}
			onClick={onClick}
			label={label}
			labelSize={labelSize}
			className={className}
			disabled={disabled}
		/>
	);
};

export default styled(Button)``;
